import store from 'app/store';
import {
  ElectricalPanelType,
  ExistingSystemComponentType,
  ExistingSystemType,
  ProjectAttachmentType
} from 'data/types';
import {
  setStructureAttachments,
  updateElectricalPanel,
  updateExistingSystemComponent
} from 'features/structure';
import axios from 'utils/api';
import { EQUIPMENT_IMAGE_TYPE } from 'utils/attachments';
import { loadStructureAttachments } from 'utils/data';
import {
  componentHasSiblingsInSystem,
  getExistingEquipmentComponents,
  isSelfContainedComponentType
} from 'utils/existing-equipment';
import { resizeImageIfNeeded } from 'utils/helpers';
import { hasUsableLabelEvidence, LabelEvidence } from 'utils/label-evidence';
import {
  determineAmperageFromAttachment,
  determineModelNumberFromAttachment,
  determineSystemComponentFromLabelEvidence,
  determineSystemComponentFromModelNumber,
  ModelNumberAnalysis,
  systemComponentIsValid
} from 'utils/systems';

export async function uploadEquipmentImages(files: File[]): Promise<number[]> {
  const { structureId, structureUniqueId } = store.getState().structure;
  if (!structureId || !structureUniqueId || files.length === 0) {
    return [];
  }

  const formData = new FormData();
  formData.append('structureUniqueId', structureUniqueId);
  formData.append('imageType', EQUIPMENT_IMAGE_TYPE);

  const processedFiles = await Promise.all(
    files.map((file) =>
      file.type.startsWith('image/') ? resizeImageIfNeeded(file) : file
    )
  );

  for (const file of processedFiles) {
    formData.append('images', file);
  }

  const { data } = await axios.post<{
    success: boolean;
    attachmentIds: number[];
  }>('structure/structure-images', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

  await loadStructureAttachments(structureId);

  return data.attachmentIds ?? [];
}

export function getAssignedEquipmentAttachments(
  component: ExistingSystemComponentType,
  structureAttachments: ProjectAttachmentType[]
): ProjectAttachmentType[] {
  const assignedIds = component.imageAttachmentIds ?? [];
  return structureAttachments.filter((attachment) =>
    assignedIds.includes(attachment.id)
  );
}

const equipmentAnalysisGeneration = new Map<string, number>();

export function isEquipmentAnalysisInFlight(uniqueId: string): boolean {
  return equipmentAnalysisGeneration.has(uniqueId);
}

function beginEquipmentAnalysis(uniqueId: string): number {
  const generation = (equipmentAnalysisGeneration.get(uniqueId) ?? 0) + 1;
  equipmentAnalysisGeneration.set(uniqueId, generation);
  return generation;
}

function isCurrentEquipmentAnalysis(
  uniqueId: string,
  generation: number
): boolean {
  return equipmentAnalysisGeneration.get(uniqueId) === generation;
}

function finishEquipmentAnalysis(uniqueId: string, generation: number): void {
  if (equipmentAnalysisGeneration.get(uniqueId) === generation) {
    equipmentAnalysisGeneration.delete(uniqueId);
  }
}

function equipmentPhotoIdsToDelete(
  component: ExistingSystemComponentType,
  existingSystems: ExistingSystemType[]
): number[] {
  const referencedByOthers = new Set(
    getExistingEquipmentComponents(existingSystems)
      .filter((other) => other.uniqueId !== component.uniqueId)
      .flatMap((other) => other.imageAttachmentIds ?? [])
  );
  return [...new Set(component.imageAttachmentIds ?? [])].filter(
    (imageId) => !referencedByOthers.has(imageId)
  );
}

export async function deleteEquipmentComponentPhotos(
  component: ExistingSystemComponentType
): Promise<void> {
  const imageIds = equipmentPhotoIdsToDelete(
    component,
    store.getState().structure.existingSystems
  );
  if (imageIds.length === 0) {
    return;
  }

  const deletedIds = new Set<number>();
  await Promise.all(
    imageIds.map(async (imageId) => {
      try {
        await axios.delete(`structure/structure-images?imageId=${imageId}`);
        deletedIds.add(imageId);
      } catch (error) {
        console.error('Error deleting equipment image:', error);
      }
    })
  );

  if (deletedIds.size === 0) {
    return;
  }

  const { structureAttachments } = store.getState().structure;
  store.dispatch(
    setStructureAttachments(
      structureAttachments.filter(
        (attachment) => !deletedIds.has(attachment.id)
      )
    )
  );
}

// Best hit across the attachments: the first one yielding a model number
// wins (with its evidence); failing all, the first usable label evidence is
// returned with a null model number.
export async function determineModelNumberFromAttachmentIds(
  attachmentIds: number[]
): Promise<ModelNumberAnalysis> {
  let fallbackEvidence: LabelEvidence | null = null;

  for (const attachmentId of attachmentIds) {
    // eslint-disable-next-line no-await-in-loop -- sequential first-hit-wins
    const { modelNumber, evidence } = await determineModelNumberFromAttachment(
      attachmentId
    );
    if (modelNumber) {
      return { modelNumber, evidence };
    }
    if (!fallbackEvidence && hasUsableLabelEvidence(evidence)) {
      fallbackEvidence = evidence;
    }
  }

  return { modelNumber: null, evidence: fallbackEvidence };
}

export function existingEquipmentComponentNeedsAnalysis(
  component: ExistingSystemComponentType
): boolean {
  // A specified type means the values were reviewed (in-modal analyzer
  // review, AHA-6681 batch 6) or hand-picked — auto re-analysis would
  // clobber user corrections just because config-required fields are still
  // missing. Only legacy untyped components keep auto-analyzing.
  if (component.typeSpecified === true) {
    return false;
  }
  const hasData =
    (component.imageAttachmentIds?.length ?? 0) > 0 || !!component.modelNumber;
  return !component.analyzing && hasData && !systemComponentIsValid(component);
}

// SC mixing guard for the card auto-analysis path (AHA-6796, cursor[bot]
// PR #4330): an analyzer-returned Self Contained type must not land on a
// component that has split siblings in its system — this path writes
// straight into the store and would otherwise bypass every UI filter. When
// the guard fires, `type` is stripped and `typeSpecified: true` is NOT set
// (the component stays legacy-untyped and keeps auto-analyzing). The
// remaining analyzed specs (modelNumber, manufacturer, tonnage, ...) are
// deliberately KEPT: they are inert without a type — every renderer/gate
// keys off the type's config and componentHasMissingRequiredDetails
// short-circuits on an unspecified type — and they stay useful when the
// user later picks a split type. Sibling truth is read fresh from the
// store at sanitize time, not from the state captured before the awaits.
function sanitizeAnalyzedComponentProps(
  uniqueId: string,
  componentProps: Partial<ExistingSystemComponentType> | null
): Partial<ExistingSystemComponentType> {
  if (!componentProps?.type) {
    return componentProps ?? {};
  }
  if (
    isSelfContainedComponentType(componentProps.type) &&
    componentHasSiblingsInSystem(
      store.getState().structure.existingSystems,
      uniqueId
    )
  ) {
    const { type: strippedScType, ...specsOnly } = componentProps;
    return specsOnly;
  }
  return { ...componentProps, typeSpecified: true };
}

export async function analyzeAndUpdateEquipmentComponent(
  uniqueId: string
): Promise<void> {
  const generation = beginEquipmentAnalysis(uniqueId);

  try {
    const { structure } = store.getState();
    const component = getExistingEquipmentComponents(
      structure.existingSystems
    ).find((c) => c.uniqueId === uniqueId);

    if (!component || !isCurrentEquipmentAnalysis(uniqueId, generation)) {
      return;
    }

    let { modelNumber } = component;
    let evidence: LabelEvidence | null = null;

    if (!modelNumber) {
      const analysis = await determineModelNumberFromAttachmentIds(
        component.imageAttachmentIds ?? []
      );
      if (!isCurrentEquipmentAnalysis(uniqueId, generation)) {
        return;
      }
      modelNumber = analysis.modelNumber ?? undefined;
      evidence = analysis.evidence;

      if (modelNumber) {
        store.dispatch(
          updateExistingSystemComponent({
            uniqueId,
            modelNumber,
            analyzing: true
          })
        );
      }
    }

    if (!isCurrentEquipmentAnalysis(uniqueId, generation)) {
      return;
    }

    if (!modelNumber) {
      if (hasUsableLabelEvidence(evidence)) {
        // Evidence-only lookup — modelNumber is deliberately absent from the
        // dispatch so a null never lands over an existing value.
        const componentProps = await determineSystemComponentFromLabelEvidence(
          evidence
        );
        if (!isCurrentEquipmentAnalysis(uniqueId, generation)) {
          return;
        }
        store.dispatch(
          updateExistingSystemComponent({
            uniqueId,
            ...sanitizeAnalyzedComponentProps(uniqueId, componentProps),
            analyzing: false
          })
        );
        return;
      }
      store.dispatch(
        updateExistingSystemComponent({ uniqueId, analyzing: false })
      );
      return;
    }

    const componentProps = hasUsableLabelEvidence(evidence)
      ? await determineSystemComponentFromModelNumber(
          modelNumber,
          undefined,
          evidence
        )
      : await determineSystemComponentFromModelNumber(modelNumber);

    if (!isCurrentEquipmentAnalysis(uniqueId, generation)) {
      return;
    }

    store.dispatch(
      updateExistingSystemComponent({
        uniqueId,
        ...sanitizeAnalyzedComponentProps(uniqueId, componentProps),
        modelNumber,
        analyzing: false
      })
    );
  } catch (error) {
    console.error('Error analyzing equipment component:', error);
    if (!isCurrentEquipmentAnalysis(uniqueId, generation)) {
      return;
    }
    store.dispatch(
      updateExistingSystemComponent({ uniqueId, analyzing: false })
    );
  } finally {
    finishEquipmentAnalysis(uniqueId, generation);
  }
}

async function determineAmperageFromAttachmentIds(
  attachmentIds: number[]
): Promise<number | null> {
  const [attachmentId, ...remainingAttachmentIds] = attachmentIds;

  if (attachmentId === undefined) {
    return null;
  }

  const amperage = await determineAmperageFromAttachment(attachmentId);

  return amperage ?? determineAmperageFromAttachmentIds(remainingAttachmentIds);
}

export function electricalPanelNeedsAnalysis(
  panel: ElectricalPanelType
): boolean {
  const hasData = (panel.imageAttachmentIds?.length ?? 0) > 0;
  return !panel.analyzing && hasData && panel.amperage === undefined;
}

export async function analyzeAndUpdateElectricalPanel(
  uniqueId: string
): Promise<void> {
  const { structure } = store.getState();
  const panel = structure.structure
    .flatMap((level) => level.electricalPanels)
    .find((p) => p.uniqueId === uniqueId);

  if (!panel) {
    return;
  }

  try {
    const amperage = await determineAmperageFromAttachmentIds(
      panel.imageAttachmentIds ?? []
    );
    store.dispatch(
      updateElectricalPanel({
        uniqueId,
        updates: {
          ...(amperage !== null ? { amperage } : {}),
          analyzing: false
        }
      })
    );
  } catch (error) {
    console.error('Error analyzing electrical panel:', error);
    store.dispatch(
      updateElectricalPanel({ uniqueId, updates: { analyzing: false } })
    );
  }
}
